// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { GridsterItem } from 'angular-gridster2';
import { append, patch, updateItem } from '@ngxs/store/operators';

// Services
import { SpinnerService } from '../../../../../shared/components/spinner/services/spinner.service';
import { DashboardService } from '../services/dashboard.service';
import { CoreService } from '../../../../../shared/services';

// Models
import { DashboardDto } from '../models/dashboard-dto';
import { AppCleanup } from '../../../../../shared/models';

// Store
import { DashboardActions } from './dashboard.action';

// Enums
import { StatusType } from '../../../../../shared/enums';

// Helpers
import { UtilityHelper } from '../../../../../shared/helpers';

export interface DashboardStateModel {
	items: DashboardDto[];
	loaded: boolean;
	currentDashboard: DashboardDto;
	currentDashboardGridsterItems: GridsterItem[];
}

const defaultState: DashboardStateModel = {
	items: undefined,
	loaded: false,
	currentDashboard: undefined,
	currentDashboardGridsterItems: undefined,
};

@State<any>({
	name: 'dashboard',
	defaults: defaultState,
})
@Injectable()
export class DashboardState extends AppCleanup {
	constructor(
		private spinnerService: SpinnerService,
		private dashboardService: DashboardService,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();
	}

	@Action(DashboardActions.GetAll)
	getAll(ctx: StateContext<DashboardStateModel>, action: DashboardActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.dashboardService
			.getDashboards(action.data.jobKey)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (dashboards) => {
					ctx.dispatch(new DashboardActions.SetAll(dashboards));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.DASHBOARD.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(DashboardActions.SetAll)
	setAll(ctx: StateContext<DashboardStateModel>, action: DashboardActions.SetAll) {
		ctx.setState(
			patch({
				items: action.dashboards,
				loaded: true,
			})
		);
	}

	@Action(DashboardActions.CreateDashboard)
	createDashboard(ctx: StateContext<DashboardStateModel>, action: DashboardActions.CreateDashboard) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.dashboardService
			.createDashboard(action.data.jobKey, action.data.dashboard)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (dashboard) => {
					ctx.setState(
						patch({
							items: append([dashboard]),
						})
					);

					ctx.dispatch(new DashboardActions.CreateDashboardSuccess(dashboard));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.CREATED', { name: this.translateService.instant('MODULE.DASHBOARD.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new DashboardActions.CreateDashboardError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_CREATED', { name: this.translateService.instant('MODULE.DASHBOARD.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(DashboardActions.UpdateOrCopyDashboard)
	updateDashboard(ctx: StateContext<DashboardStateModel>, action: DashboardActions.UpdateOrCopyDashboard) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.dashboardService
			.updateDashboard(action.data.jobKey, action.data.dashboard)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (dashboard) => {
					if (action.data.copy) {
						ctx.setState(
							patch({
								items: append([dashboard]),
							})
						);
					} else {
						const currentState = ctx.getState();
						const existingItem = currentState.items?.find((item) => item.id === dashboard.id);

						if (existingItem) {
							ctx.setState(
								patch({
									items: updateItem((d) => d.id === action.data.dashboard.id, dashboard),
								})
							);
						}
					}

					ctx.dispatch(new DashboardActions.UpdateOrCopyDashboardSuccess(dashboard));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						let message;

						if (action.data.copy) {
							message = `${this.translateService.instant('GENERIC.COPIED', { name: this.translateService.instant('MODULE.DASHBOARD.TITLE_CAPITAL') })}`;
						} else {
							message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.DASHBOARD.TITLE_CAPITAL') })}`;
						}

						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new DashboardActions.UpdateOrCopyDashboardError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						let message;

						if (action.data.copy) {
							message = `${this.translateService.instant('GENERIC.NOT_COPIED', { name: this.translateService.instant('MODULE.DASHBOARD.TITLE_CAPITAL') })}`;
						} else {
							message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.DASHBOARD.TITLE_CAPITAL') })}`;
						}

						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(DashboardActions.SetCurrentDashboard)
	setCurrentDashboard(ctx: StateContext<DashboardStateModel>, action: DashboardActions.SetCurrentDashboard) {
		ctx.setState(
			patch({
				currentDashboard: action.dashboard,
			})
		);
	}

	@Action(DashboardActions.SetCurrentDashboardGridsterItems)
	setCurrentDashboardGridsterItems(ctx: StateContext<DashboardStateModel>, action: DashboardActions.SetCurrentDashboardGridsterItems) {
		ctx.setState(
			patch({
				currentDashboardGridsterItems: action.items,
			})
		);
	}

	@Selector()
	static isLoaded(state: DashboardStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static dashboards(state: DashboardStateModel): DashboardDto[] {
		return state.items;
	}

	@Selector()
	static currentDashboard(state: DashboardStateModel): DashboardDto {
		return state.currentDashboard;
	}

	@Selector()
	static currentDashboardGridsterItems(state: DashboardStateModel): GridsterItem[] {
		return state.currentDashboardGridsterItems;
	}
}
